/**
 * Jade Phoenix — Global JavaScript
 * Loaded via layout/theme.liquid on all full-layout pages.
 *
 * Behaviors:
 * - Scroll reveal (IntersectionObserver, data-jp-reveal)
 * - Mobile nav toggle (data-jp-toggle)
 * - Header scroll shadow (data-jp-scroll-header)
 * - Stats counter (data-jp-count-target)
 * - Announcement bar dismiss (data-jp-dismiss)
 * - prefers-reduced-motion detection
 *
 * All selectors use data-jp-* attributes per AR15.
 * Scoped via IIFE per AR16.
 */
(function() {
  'use strict';

  /* ── Reduced Motion Detection ── */

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var prefersReducedMotion = motionQuery.matches;

  motionQuery.addEventListener('change', function(e) {
    prefersReducedMotion = e.matches;
  });

  /* ── Scroll Reveal System (data-jp-reveal) ── */

  function initScrollReveal() {
    var elements = document.querySelectorAll('[data-jp-reveal]');
    if (!elements.length) return;

    if (prefersReducedMotion) {
      elements.forEach(function(el) {
        el.classList.add('revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var delay = el.getAttribute('data-jp-reveal-delay');
          if (delay) {
            el.style.transitionDelay = (parseInt(delay, 10) * 100) + 'ms';
          }
          el.classList.add('revealed');
          observer.unobserve(el);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '-50px'
    });

    elements.forEach(function(el) {
      observer.observe(el);
    });
  }

  /* ── Mobile Nav Toggle (data-jp-toggle="mobile-nav") ── */

  function initMobileNav() {
    var toggleButtons = document.querySelectorAll('[data-jp-toggle="mobile-nav"]');
    var overlay = document.querySelector('[data-jp-mobile-nav]');
    if (!toggleButtons.length || !overlay) return;

    var isOpen = false;
    var previouslyFocused = null;

    function openNav() {
      isOpen = true;
      overlay.classList.add('jp-mobile-nav--open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      toggleButtons.forEach(function(btn) {
        btn.setAttribute('aria-expanded', 'true');
      });

      previouslyFocused = document.activeElement;

      // Focus the close button after transition
      var closeBtn = overlay.querySelector('[data-jp-toggle="mobile-nav"]');
      if (closeBtn) {
        setTimeout(function() { closeBtn.focus(); }, 100);
      }
    }

    function closeNav() {
      isOpen = false;
      overlay.classList.remove('jp-mobile-nav--open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      toggleButtons.forEach(function(btn) {
        btn.setAttribute('aria-expanded', 'false');
      });

      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    }

    function toggleNav() {
      if (isOpen) {
        closeNav();
      } else {
        openNav();
      }
    }

    // Bind toggle buttons
    toggleButtons.forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        toggleNav();
      });
    });

    // Close on Escape
    document.addEventListener('keydown', function(e) {
      if (isOpen && e.key === 'Escape') {
        closeNav();
      }
    });

    // Close when nav links inside overlay are clicked
    var navLinks = overlay.querySelectorAll('a');
    navLinks.forEach(function(link) {
      link.addEventListener('click', function() {
        closeNav();
      });
    });

    // Focus trap
    document.addEventListener('keydown', function(e) {
      if (!isOpen || e.key !== 'Tab') return;

      var focusable = overlay.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;

      var firstFocusable = focusable[0];
      var lastFocusable = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    });
  }

  /* ── Header Scroll Shadow (data-jp-scroll-header) ── */

  function initHeaderScroll() {
    var header = document.querySelector('[data-jp-scroll-header]');
    if (!header) return;

    var scrolled = false;
    var ticking = false;

    function updateHeader() {
      var shouldBeScrolled = window.scrollY > 60;
      if (shouldBeScrolled !== scrolled) {
        scrolled = shouldBeScrolled;
        if (scrolled) {
          header.classList.add('jp-header-scrolled');
        } else {
          header.classList.remove('jp-header-scrolled');
        }
      }
      ticking = false;
    }

    window.addEventListener('scroll', function() {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }, { passive: true });

    // Check initial state
    updateHeader();
  }

  /* ── Stats Counter (data-jp-count-target) ── */

  function initStatsCounter() {
    var counters = document.querySelectorAll('[data-jp-count-target]');
    if (!counters.length) return;

    if (prefersReducedMotion) {
      counters.forEach(function(el) {
        var target = parseFloat(el.getAttribute('data-jp-count-target')) || 0;
        var prefix = el.getAttribute('data-jp-count-prefix') || '';
        var suffix = el.getAttribute('data-jp-count-suffix') || '';
        var decimals = countDecimals(target);
        el.textContent = prefix + target.toFixed(decimals) + suffix;
      });
      return;
    }

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });

    counters.forEach(function(el) {
      observer.observe(el);
    });
  }

  function countDecimals(num) {
    var str = String(num);
    var dot = str.indexOf('.');
    return dot === -1 ? 0 : str.length - dot - 1;
  }

  function animateCounter(el) {
    var target = parseFloat(el.getAttribute('data-jp-count-target')) || 0;
    var prefix = el.getAttribute('data-jp-count-prefix') || '';
    var suffix = el.getAttribute('data-jp-count-suffix') || '';
    var decimals = countDecimals(target);
    var duration = 1500;
    var startTime = null;

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var elapsed = timestamp - startTime;
      var progress = Math.min(elapsed / duration, 1);

      var eased = 1 - Math.pow(1 - progress, 3);
      var current = eased * target;

      el.textContent = prefix + current.toFixed(decimals) + suffix;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  }

  /* ── Announcement Dismiss (data-jp-dismiss) ── */

  function initDismiss() {
    var dismissButtons = document.querySelectorAll('[data-jp-dismiss]');
    if (!dismissButtons.length) return;

    var header = document.querySelector('[data-jp-scroll-header]');

    dismissButtons.forEach(function(btn) {
      var key = btn.getAttribute('data-jp-dismiss');

      // If already dismissed this session, the inline script in
      // announcement-bar.liquid already hid the bar and set bar-dismissed
      // on the header — nothing to do here except skip binding.
      if (sessionStorage.getItem(key) === 'true') return;

      btn.addEventListener('click', function() {
        sessionStorage.setItem(key, 'true');

        var targetId = btn.getAttribute('data-jp-target');
        var target = targetId ? document.getElementById(targetId) : btn.closest('.jp-announcement');

        // Animate the announcement bar out, then hide it and the section wrapper
        if (target) {
          target.classList.add('jp-dismissed');
          setTimeout(function() {
            target.style.display = 'none';
            var sectionWrapper = target.closest('.shopify-section');
            if (sectionWrapper) sectionWrapper.style.display = 'none';
          }, prefersReducedMotion ? 0 : 320);
        }

        // Slide the header up with a one-time transition
        if (header) {
          header.style.transition = 'top 0.3s ease, box-shadow 0.3s ease';
          setTimeout(function() { header.style.transition = ''; }, 350);
        }
        document.documentElement.classList.add('jp-bar-dismissed');
      });
    });
  }

  /* ── Education Hero Parallax (data-jp-parallax) ── */

  function initParallax() {
    var hero = document.querySelector('[data-jp-parallax]');
    if (!hero || prefersReducedMotion) return;

    var img = hero.querySelector('.jp-education-hero__inner');
    if (!img) return;

    var ticking = false;
    window.addEventListener('scroll', function() {
      if (!ticking) {
        requestAnimationFrame(function() {
          var rect = hero.getBoundingClientRect();
          if (rect.bottom > 0 && rect.top < window.innerHeight) {
            var progress = -rect.top / window.innerHeight;
            img.style.transform = 'translateY(' + (progress * 40) + 'px)';
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ── Product Image Zoom (data-jp-zoom) ── */

  function initProductZoom() {
    var container = document.querySelector('[data-jp-zoom]');
    if (!container || prefersReducedMotion) return;

    var img = container.querySelector('img');
    if (!img) return;

    container.style.overflow = 'hidden';
    container.style.cursor = 'zoom-in';
    img.style.transition = 'transform 350ms cubic-bezier(0.22, 1, 0.36, 1)';

    var zoomed = false;

    container.addEventListener('click', function(e) {
      if (!zoomed) {
        var rect = container.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
        img.style.transform = 'scale(2)';
        container.style.cursor = 'zoom-out';
        zoomed = true;
      } else {
        img.style.transform = 'scale(1)';
        container.style.cursor = 'zoom-in';
        zoomed = false;
      }
    });

    container.addEventListener('mousemove', function(e) {
      if (!zoomed) return;
      var rect = container.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = x + '% ' + y + '%';
    });
  }

  /* ── Initialize all behaviors on DOM ready ── */

  function init() {
    initScrollReveal();
    initMobileNav();
    initHeaderScroll();
    initStatsCounter();
    initDismiss();
    initParallax();
    initProductZoom();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
